import startOfDay from 'date-fns/startOfDay';
import subYears from 'date-fns/subYears';
import getUnixTimestamp from '@framework/date-helpers/getUnixTimestamp';
import getUTCDate from '@framework/date-helpers/getUTCDate';
import getLocalDate from '@framework/date-helpers/getLocalDate';
import { DEFAULT_TTM_SELECT_VALUE, MapCompanyRole, TypeCompany, REDIRECT_ERROR_URL_WRONG_ID } from './constants';
import {
  IPeriodFilter,
  IScorecardCompanyData,
  IScorecardState,
  ITtmFilter,
  ITtmFilterResponse,
} from '@/creditflowresearch/application/League/interfaces';
import { IStandingsResponse } from '@/creditflowresearch/domain/vo/League/IStandings';
import map from 'lodash/map';
import filter from 'lodash/filter';
import { isChangedProductsRegions } from '@/creditflowresearch/application/helpers';
import { filterSupportedProducts } from '@/creditflowresearch/application/Dictionaries/helpers';
import { addQueryParams, getQueryString } from '@framework/router/nextRouter';

export const defaultResponse = { collection: [], totalCount: 0 };

export const defaultTtmFilter = {
  disabled: false,
  dateFrom: getUnixTimestamp(getLocalDate(startOfDay(subYears(getUTCDate(), 1)))),
  dateTo: null,
  label: DEFAULT_TTM_SELECT_VALUE,
  value: DEFAULT_TTM_SELECT_VALUE,
};

export const defaultTtmFilterResponse = {
  active: true,
  dateFrom: getUnixTimestamp(getLocalDate(startOfDay(subYears(getUTCDate(), 1)))),
  dateTo: null,
  filterName: DEFAULT_TTM_SELECT_VALUE,
};

export const mapGetPeriodsResult = (result:ITtmFilterResponse[]):ITtmFilter[] => result.map(
  (filter) => (
    {
      value: filter.filterName,
      label: filter.filterName,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      disabled: !filter.active,
    }
  ),
);

export const combineReducers = (slices) => (state, action) => Object.keys(slices).reduce(
    (acc, prop) => ({
      ...acc,
      [prop]: slices[prop](acc[prop], action),
    }),
    state,
  );

export const getTtmFilterInitialState = (ttmFilter:ITtmFilter[]):IPeriodFilter => {
  const collection = ttmFilter.length > 0 ? ttmFilter : [defaultTtmFilter];
  const selected = collection.find((filter) => filter.value === DEFAULT_TTM_SELECT_VALUE) || defaultTtmFilter;

  return {
    isCollectionFetching: false,
    collection,
    selected,
  };
};

interface IScorecardInitialStateProps {
  scorecardData: IStandingsResponse,
  companyData: IScorecardCompanyData,
}

export const getScorecardInitialState = ({
  scorecardData,
  companyData,
}:IScorecardInitialStateProps): IScorecardState => ({
  data: scorecardData,
  isFetching: false,
  initialized: !!Object.keys(scorecardData).length,
  expandedCategories: [],
  columnState: {
    valueTableIndex: 0,
    valueTableColumnCount: 4,
    rankTableIndex: 0,
    rankTableColumnCount: 4,
  },
  selectedPeriod: {
    categoryId: null,
    subCategoryId: null,
    name: '',
    dateFrom: null,
    dateTo: null,
  },
  company: {
    data: { ...companyData },
    error: companyData.id ? null : REDIRECT_ERROR_URL_WRONG_ID,
    isCompanyFetching: false,
  },
});

interface IRegionsAndProducts {
  productIds: string[],
  regionIds: string[],
}

interface IRedirect {
  redirect: {
    permanent: boolean,
    destination: string,
  },
}

const toQueryParamString = (value: string | string[] | undefined): string => (
  Array.isArray(value) ? value.join(',') : value ?? ''
);

export const getRegionsAndProducts = (context):IRegionsAndProducts | IRedirect => {
  const { query, productsRegions, domainDictionaries } = context;
  const { products, regions } = query;
  const productsList = filterSupportedProducts(domainDictionaries.products ?? []);
  const regionsList = domainDictionaries.regions ?? [];
  const { productIds, regionIds } = productsRegions;
  const validProducts = map(filter(productsList, (item) => productIds.includes(item.id)), 'abbreviation');
  const validRegions = map(filter(regionsList, (item) => regionIds.includes(item.id)), 'abbreviation');

  const changedProductsRegions = isChangedProductsRegions(
    toQueryParamString(regions), toQueryParamString(products), validRegions, validProducts,
  );

  if (changedProductsRegions) {
    const { tab, leagueType, viewId, companyName, ...restParams } = query;
    const newQuery = addQueryParams(restParams, { products: validProducts, regions: validRegions });

    return {
      redirect: {
        permanent: false,
        destination: `?${ getQueryString(newQuery) }`,
      },
    };
  }

  return {
    productIds,
    regionIds,
  };
};

export const getDefaultResponse = ({ page, perPage, response, ...other }) => ({
  data: response.collection,
  isFetching: false,
  totalCount: response.totalCount,
  page,
  perPage,
  ...other,
});

export const getLeagueCompaniesPayload = (context) => {
  const { companyId, leagueType } = context;

  if (MapCompanyRole[leagueType] === TypeCompany.underwriter) return { includedUnderwriterIds: [companyId] };
  if (MapCompanyRole[leagueType] === TypeCompany.ratingAgency) return { includedRatingAgencyIds: [companyId] };

  return null;
};
